import api from "./api";
import { ok, isoUtc, nameOf, reasonOf, getMe, can, ORG_SCOPES, myRecordSummary, DIRECTION_TO_API, DIRECTION_TO_UI, FREQUENCY_TO_API, FREQUENCY_TO_UI } from "./core";

export const PROPOSAL_STATUS = {
  DRAFT: { label: "Draft", tone: "neutral" },
  PENDING_APPROVAL: { label: "Awaiting manager", tone: "info" },
  RETURNED: { label: "Returned for changes", tone: "warning" },
  APPROVED: { label: "Approved", tone: "success" },
  REJECTED: { label: "Rejected", tone: "danger" },
  WITHDRAWN: { label: "Withdrawn", tone: "neutral" },
};

export async function getActiveKras({ departmentId, search } = {}) {
  const { data } = await api.get("/library/kra/active", { params: { department_id: departmentId, search } });
  return ok(data.map((k) => ({ id: String(k.kra_id), code: k.kra_code, name: k.kra_name, description: k.description || "", kpiCount: k.kpi_count })));
}

export async function getKpisForKra(kraId, { includeInactive = false } = {}) {
  if (!kraId) return ok([]);
  const { data } = await api.get(`/library/kra/${kraId}/kpis`, { params: { include_inactive: includeInactive || undefined } });
  return ok(data.map((k) => ({
    id: String(k.kpi_id), code: k.kpi_code, name: k.kpi_name, description: k.description || "", unit: k.measurement_unit,
    frequency: FREQUENCY_TO_UI[k.measurement_frequency] || "Once", direction: DIRECTION_TO_UI[k.measurement_direction] || "higher",
    active: Boolean(k.is_active),
  })));
}

export async function getKraKpiTree({ includeInactive = false } = {}) {
  const { data } = await api.get("/library/kra/tree", { params: { include_inactive: includeInactive || undefined } });
  const kpi = (k) => ({ id: String(k.kpi_id), code: k.kpi_code, name: k.kpi_name, active: Boolean(k.is_active), parentKraId: k.parent_kra_id ? String(k.parent_kra_id) : null });
  return ok({
    kras: data.kras.map((k) => ({ id: String(k.kra_id), code: k.kra_code, name: k.kra_name, active: Boolean(k.is_active), kpis: k.kpis.map(kpi) })),
    unmapped: data.unmapped_kpis.map(kpi),
  });
}

export const setKpiParentKra = (kpiId, kraId, reason) =>
  api.put(`/library/kpi/${kpiId}/parent-kra`, { parent_kra_id: kraId ? Number(kraId) : null, reason: reasonOf(reason, kraId ? "KPI mapped to KRA" : "KPI unmapped from KRA") });

export async function getMyKraKpiTree(cycleId) {
  const summary = await myRecordSummary(cycleId);
  const { data } = await api.get(`/records/${summary.performance_record_id}/kra-kpi`);
  const item = (x) => ({ id: String(x.performance_item_id), name: x.item_title, weightage: Number(x.weight || 0), target: x.target, unit: x.measurement_unit, source: x.source_type });
  return ok({
    recordId: summary.performance_record_id,
    kras: data.kras.map((k) => ({ ...item(k), kpis: k.kpis.map(item) })),
    unmapped: data.unmapped_kpis.map(item),
  });
}

const toProposal = (p) => ({
  id: p.proposal_id,
  type: p.proposal_type,
  employeeId: p.employee_id,
  employeeName: p.employee_name || nameOf(p.employee_id),
  recordId: p.performance_record_id,
  kraId: p.kra_id ? String(p.kra_id) : "",
  kraName: p.kra_name || "",
  kpiId: p.kpi_id ? String(p.kpi_id) : "",
  kpiName: p.kpi_name || "",
  parentProposalId: p.parent_proposal_id ? String(p.parent_proposal_id) : "",
  parentProposalTitle: p.parent_proposal_title || "",
  title: p.title,
  description: p.description || "",
  unit: p.measurement_unit,
  direction: DIRECTION_TO_UI[p.measurement_direction] || "higher",
  frequency: FREQUENCY_TO_UI[p.measurement_frequency] || "Once",
  weightage: p.weight === null || p.weight === undefined ? "" : Number(p.weight),
  target: p.target === null || p.target === undefined ? "" : Number(p.target),
  dueDate: p.due_date || "",
  status: p.status,
  statusLabel: PROPOSAL_STATUS[p.status]?.label || p.status,
  tone: PROPOSAL_STATUS[p.status]?.tone || "neutral",
  reviewerId: p.reviewer_employee_id,
  decidedBy: p.decided_by_name || (p.decided_by_employee_id ? nameOf(p.decided_by_employee_id) : ""),
  decisionReason: p.decision_reason || "",
  submittedAt: isoUtc(p.submitted_at),
  decidedAt: isoUtc(p.decided_at),
  lastEditedBy: p.last_edited_by_employee_id,
  version: p.version_no,
  approvedItemId: p.approved_item_id,
  history: (p.history || []).map((h) => ({ event: h.event_type, by: nameOf(h.actor_employee_id), reason: h.reason, at: isoUtc(h.created_at) })),
});

function proposalFields(form) {
  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
  return {
    performance_record_id: form.recordId ? Number(form.recordId) : null,
    kra_id: form.kraId ? Number(form.kraId) : null,
    kpi_id: form.type === "KPI" && form.kpiId ? Number(form.kpiId) : null,
    parent_proposal_id: form.type === "KPI" && form.parentProposalId ? Number(form.parentProposalId) : null,
    title: String(form.title || "").trim(),
    description: String(form.description || "").trim(),
    measurement_unit: form.unit || "%",
    measurement_direction: DIRECTION_TO_API[form.direction] || "HIGHER_IS_BETTER",
    measurement_frequency: FREQUENCY_TO_API[form.frequency] || "ONCE",
    weight: num(form.weightage),
    target: num(form.target),
    due_date: form.dueDate || null,
  };
}

export async function getProposalPermissions() {
  const me = await getMe();
  return ok({
    canPropose: can(me, "kra_kpi.self.propose"),
    canReview: can(me, "kra_kpi.team.review", ["TEAM", "SYSTEM_ALL"]),
    canSeeAll: can(me, "performance.org.read", ORG_SCOPES),
  });
}

export async function listProposals({ view = "all", status } = {}) {
  const { data } = await api.get("/kra-kpi-proposals", { params: { view, status, limit: 200 } });
  return ok(data.map(toProposal));
}

export async function getProposal(id) {
  const { data } = await api.get(`/kra-kpi-proposals/${id}`);
  return ok(toProposal(data));
}

export async function createProposal(form, { submit = false } = {}) {
  const { data } = await api.post("/kra-kpi-proposals", {
    proposal_type: form.type,
    ...proposalFields(form),
    submit,
    reason: reasonOf(form.reason, submit ? `${form.type} proposed for approval` : `${form.type} proposal drafted`),
  });
  return ok(toProposal(data));
}

export async function updateProposal(proposal, form) {
  const { data } = await api.put(`/kra-kpi-proposals/${proposal.id}`, {
    ...proposalFields({ ...form, type: proposal.type }),
    expected_version: proposal.version,
    reason: reasonOf(form.reason, "Proposal updated"),
  });
  return ok(toProposal(data));
}

export const submitProposal = (id, reason) => api.post(`/kra-kpi-proposals/${id}/submit`, { reason: reasonOf(reason, "Sent for approval") });
export const withdrawProposal = (id, reason) => api.post(`/kra-kpi-proposals/${id}/withdraw`, { reason: reasonOf(reason, "Proposal withdrawn") });

export function decideProposal(proposal, decision, comment) {
  const text = String(comment || "").trim();
  if (decision !== "APPROVE" && !text) throw new Error("Tell the employee why (this comment is shown to them).");
  return api.post(`/kra-kpi-proposals/${proposal.id}/decision`, {
    decision,
    expected_version: proposal.version,
    reason: reasonOf(text, "Approved"),
  });
}
